// Gestion de la couleur d'accentuation utilisateur.
// Surcharge les CSS variables --accent / --accent-hover / --accent-weak / --accent-strong
// définies dans index.css, au runtime sur document.documentElement.

export const DEFAULT_ACCENT = '#10a37f';
const STORAGE_KEY = 'userAccentColor';

// localStorage peut throw (Safari en navigation privée, quotas, cookies bloqués).
// La couleur d'accent est purement cosmétique : un échec ne doit jamais empêcher
// l'app de démarrer, on retombe silencieusement sur le thème par défaut.
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* préférence non persistée : sans conséquence sur la session courante */
  }
};

const safeRemoveItem = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* idem */
  }
};

const hexToRgb = (hex: string): [number, number, number] => {
  // isValidHex n'accepte que la forme 6 chiffres : le développement du raccourci
  // 3 chiffres est donc inatteignable par les appelants publics. Conservé comme
  // garde-fou pour un usage interne direct, sans coût.
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
};

const mixWithBlack = (hex: string, amount = 0.12): string => {
  const [r, g, b] = hexToRgb(hex);
  const nr = Math.round(r * (1 - amount));
  const ng = Math.round(g * (1 - amount));
  const nb = Math.round(b * (1 - amount));
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
};

const toRgba = (hex: string, alpha: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const isValidHex = (hex: string): boolean => /^#[0-9a-fA-F]{6}$/.test(hex);

export const setAccentColor = (hex: string) => {
  if (!isValidHex(hex)) return;
  const root = document.documentElement;
  root.style.setProperty('--accent', hex);
  root.style.setProperty('--accent-hover', mixWithBlack(hex, 0.12));
  root.style.setProperty('--accent-weak', toRgba(hex, 0.10));
  root.style.setProperty('--accent-strong', toRgba(hex, 0.22));
};

export const saveAccentColor = (hex: string) => {
  if (!isValidHex(hex)) return;
  safeSetItem(STORAGE_KEY, hex);
  setAccentColor(hex);
};

export const resetAccentColor = () => {
  safeRemoveItem(STORAGE_KEY);
  const root = document.documentElement;
  root.style.removeProperty('--accent');
  root.style.removeProperty('--accent-hover');
  root.style.removeProperty('--accent-weak');
  root.style.removeProperty('--accent-strong');
};

export const loadAccentColor = (): string => {
  const stored = safeGetItem(STORAGE_KEY);
  return stored && isValidHex(stored) ? stored : DEFAULT_ACCENT;
};

export const applyStoredAccentColor = () => {
  const stored = safeGetItem(STORAGE_KEY);
  if (stored && isValidHex(stored)) {
    setAccentColor(stored);
  }
};

// Palette suggérée pour l'UI
export const ACCENT_PALETTE = [
  '#10a37f', // Vert (défaut)
  '#14b8a6', // Teal
  '#3b82f6', // Bleu
  '#6366f1', // Indigo
  '#a855f7', // Violet
  '#ec4899', // Rose
  '#f97316', // Orange
  '#dc2626', // Rouge
];
